import { useId } from 'react';
import { Field, FieldDescription, FieldError, FieldLabel } from '@monksflow/monks-ui';
import { TagInput } from '@/shared/ui/TagInput';
import type { TagInputFieldProps } from './TagInputField.types';

/**
 * Labelled wrapper around the `TagInput` combobox: it owns the label/description/
 * error row so callers driving react-hook-form keep a single flat prop set
 * instead of composing `Field` slots themselves.
 */
export function TagInputField<T = string>(props: TagInputFieldProps<T>) {
  const {
    value,
    onChange,
    options,
    label,
    placeholder,
    helperText,
    error,
    required,
    disabled = false,
    freeSolo = false,
    getOptionLabel,
    getOptionValue,
    isOptionEqualToValue,
    renderOption,
    renderChip,
    id,
  } = props;

  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hasError = error !== undefined && error !== '';
  const messageId = `${inputId}-message`;
  const hasMessage = hasError || helperText !== undefined;

  return (
    <Field data-invalid={error ? true : undefined}>
      {label !== undefined && (
        <FieldLabel htmlFor={inputId}>
          {label}
          {required === true && (
            <span aria-hidden="true" className="text-destructive">
              *
            </span>
          )}
        </FieldLabel>
      )}
      <TagInput<T>
        id={inputId}
        aria-invalid={hasError || undefined}
        aria-describedby={hasMessage ? messageId : undefined}
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
        freeSolo={freeSolo}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        isOptionEqualToValue={isOptionEqualToValue}
      >
        <TagInput.Chips<T> placeholder={placeholder} required={required} renderChip={renderChip} />
        <TagInput.Content<T> renderOption={renderOption} />
      </TagInput>
      {hasError ? (
        <FieldError id={messageId}>{error}</FieldError>
      ) : helperText !== undefined ? (
        <FieldDescription id={messageId}>{helperText}</FieldDescription>
      ) : null}
    </Field>
  );
}
